import type { RouteRecordRaw, RouteRecordRedirectOption } from 'vue-router';
import { type RouteRecord } from '@crm-lib/types';
import { type StyledFontAwesomeIcons } from '@shared/plugin/icons/types';
import { type CrmDomainDomainAdviseursAdviseurType as AdviseurType } from '@services/crm-public/orval-api/model/crmDomainDomainAdviseursAdviseurType';
import { adviseurIcons } from '@crm-lib/adviseurs/adviseurbeheer/icons';

export type AdviseurMenuGroup = 'Algemeen' | 'Profiel';

export type AdviseurbeheerRouteRecord = RouteRecordRaw & {
  meta: {
    title: string;
    label: string;
    icon: StyledFontAwesomeIcons;
    pagina: string;
    group: AdviseurMenuGroup;
    default?: boolean;
    toonVoorTypes?: AdviseurType[];
  };
};

const NIET_VOOR_MAATSCHAPPIJ: AdviseurType[] = ['Regulier', 'Demo', 'Intern'];

const fullPageTitle = (tabName: string) => `Adviseurbeheer - ${tabName}`;

const createRoute = (
  tabName: string,
  icon: StyledFontAwesomeIcons,
  pagina: string,
  options?: {
    pageTitle?: string;
    children?: RouteRecord[];
    redirect?: RouteRecordRedirectOption;
    group?: AdviseurMenuGroup;
    toonVoorTypes?: AdviseurType[];
  },
): AdviseurbeheerRouteRecord => {
  const capitalisedName =
    tabName[0].toUpperCase() + tabName.slice(1).replace(/-([a-zA-Z])/g, (_, x) => x.toUpperCase());

  return {
    path: tabName,
    redirect: options?.redirect,
    name: `adviseur-${tabName}`,
    meta: {
      title: fullPageTitle(options?.pageTitle || capitalisedName),
      label: options?.pageTitle || capitalisedName,
      icon,
      pagina,
      group: options?.group ?? 'Algemeen',
      toonVoorTypes: options?.toonVoorTypes,
    },
    children: options?.children ?? [],
    component: () => import('@crm-lib/adviseurs/adviseurbeheer/AdviseurEmbed.vue'),
  };
};

const markDefault = (route: AdviseurbeheerRouteRecord): AdviseurbeheerRouteRecord => {
  route.meta.default = true;
  return route;
};

const routes: AdviseurbeheerRouteRecord[] = [
  markDefault(createRoute('algemeen', adviseurIcons.algemeen, 'algemeen/algemeen')),
  createRoute('medewerkers', adviseurIcons.medewerkers, 'algemeen/medewerkers'),
  createRoute('afdelingen', adviseurIcons.afdelingen, 'algemeen/afdelingen', {
    toonVoorTypes: ['Intern'],
  }),
  createRoute('DVO', adviseurIcons.DVO, 'algemeen/dvo', {
    toonVoorTypes: NIET_VOOR_MAATSCHAPPIJ,
  }),
  createRoute('vergunningen', adviseurIcons.vergunningen, 'algemeen/vergunningen', {
    toonVoorTypes: NIET_VOOR_MAATSCHAPPIJ,
  }),
  createRoute('grootboeken', adviseurIcons.grootboeken, 'algemeen/grootboeken', {
    toonVoorTypes: NIET_VOOR_MAATSCHAPPIJ,
  }),
  createRoute('maatschappijen', adviseurIcons.maatschappijen, 'algemeen/maatschappijen', {
    toonVoorTypes: NIET_VOOR_MAATSCHAPPIJ,
  }),
  createRoute('log', adviseurIcons.log, 'algemeen/log'),
  createRoute('logo', adviseurIcons.logo, 'logo'),
  createRoute('dossier', adviseurIcons.dossier, 'dossier'),
  createRoute('webmodules', adviseurIcons.webmodules, 'webmodules', {
    toonVoorTypes: NIET_VOOR_MAATSCHAPPIJ,
  }),
  createRoute('overig', adviseurIcons.overig, 'algemeen/overig', {
    toonVoorTypes: NIET_VOOR_MAATSCHAPPIJ,
  }),
  createRoute('klantbeeld', adviseurIcons.klantbeeld, 'monitor/klantbeeld', {
    group: 'Profiel',
    toonVoorTypes: NIET_VOOR_MAATSCHAPPIJ,
  }),
  createRoute('memos', adviseurIcons.memos, 'monitor/memo', {
    pageTitle: "Memo's",
    group: 'Profiel',
    toonVoorTypes: NIET_VOOR_MAATSCHAPPIJ,
  }),
  createRoute('alerts', adviseurIcons.alerts, 'monitor/tpalerts', {
    group: 'Profiel',
    toonVoorTypes: NIET_VOOR_MAATSCHAPPIJ,
  }),
];

export default routes;
